import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import jalaliday from "jalaliday";
import Swal from "sweetalert2";
import {
  fetchAdminWithdrawRequests,
  updateWithdrawStatus,
} from "../../../api/finance";
import "../../../styles/AdminPanel/AdminWithdrawalList.css";
import {
  ATTACH_BTN,
  Icon,
  X_ICON,
} from "../../panel/user/manageShop/shared/EditShopShared";
import {
  swalError,
  swalReject,
  swalSuccess,
  swalWarning,
} from "../../../utils/swalAlert";
import { text, title } from "framer-motion/client";

dayjs.extend(jalaliday);

const PAGE_SIZE = 10;

function SaveIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M5 5.5A2.5 2.5 0 0 1 7.5 3H16l4 4v11.5a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 18.5v-13Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M8 3v4.5a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V3.2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 21v-6a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NoteIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14.5 4H7.5A2.5 2.5 0 0 0 5 6.5v11A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5V9.5L14.5 4Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M14 4v4a1.5 1.5 0 0 0 1.5 1.5H19"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="M8.5 13h7M8.5 16.5h4.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function AttachmentIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M17.5 8.5 9.7 16.3a3 3 0 1 1-4.24-4.24l8.5-8.5a2 2 0 1 1 2.83 2.83l-8.2 8.2a1 1 0 1 1-1.42-1.41l7.5-7.5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AttachmentModal({ item, onClose }) {
  if (!item) return null;
  const hasAny = item.national_card_image || item.commitment_letter_image;

  return (
    <div className="finlist-overlay" onClick={onClose}>
      <div
        className="finlist-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="مدارک کاربر"
      >
        <button
          className="finlist-modal-close"
          onClick={onClose}
          aria-label="بستن"
        >
          <Icon>{X_ICON}</Icon>
        </button>

        <h3 className="finlist-modal-title">مدارک کاربر — {item.user_phone}</h3>

        {!hasAny ? (
          <p className="finlist-empty">مدرکی بارگذاری نشده</p>
        ) : (
          <div className="finlist-grid">
            {item.national_card_image && (
              <div className="finlist-grid-item">
                <p className="finlist-grid-label">کارت ملی</p>
                <a
                  href={item.national_card_image}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="finlist-grid-link"
                >
                  {" "}
                  🔗 مشاهده / دانلود
                </a>
              </div>
            )}

            {item.commitment_letter_image && (
              <div className="finlist-grid-item">
                <p className="finlist-grid-label">تعهدنامه</p>
                <a
                  href={item.commitment_letter_image}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="finlist-grid-link"
                >
                  🔗 مشاهده / دانلود
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminWithdrawalRequests() {
  const [items, setItems] = useState([]);
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [status, setStatus] = useState("all");
  const [statusMap, setStatusMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = useState(1);

  const loadData = async () => {
    try {
      const res = await fetchAdminWithdrawRequests();
      setItems(res.data);
      const map = {};
      res.data.forEach((item) => {
        map[item.id] = item.status;
      });
      setStatusMap(map);
    } catch {
      swalError({ title: "خطا", text: "دریافت درخواست‌ها انجام نشد" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const formatPrice = (num) =>
    new Intl.NumberFormat("fa-IR").format(Number(num || 0));

  const statusLabel = (value) =>
    ({
      pending: "در حال بررسی",
      processing: "در حال انجام",
      done: "پرداخت شد",
      canceled: "لغو شده",
    })[value];

  const methodLabel = (value) =>
    ({
      card: "شماره کارت",
      sheba: "شماره شبا",
      account: "شماره حساب",
    })[value];

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const y = dayjs(item.created_at).calendar("jalali").format("YYYY");
      const m = dayjs(item.created_at).calendar("jalali").format("MM");
      if (year !== "all" && y !== year) return false;
      if (month !== "all" && m !== month) return false;
      if (status !== "all" && item.status !== status) return false;
      return true;
    });
  }, [items, year, month, status]);

  // با تغییر فیلترها، برگرد به صفحه‌ی اول
  useEffect(() => {
    setPage(1);
  }, [year, month, status]);

  const totalCount = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== pageSafe) setPage(pageSafe);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const pagedItems = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, pageSafe]);

  const handleSave = async (id) => {
    const selectedStatus = statusMap[id];
    if (selectedStatus === "done" || selectedStatus === "canceled") {
      const { note } = await swalReject({
        title: "ثبت توضیحات",
        label: "توضیحات را وارد کنید...",
        placeholder:
          selectedStatus === "done"
            ? "توضیحات (مثلاً شماره پیگیری واریز)"
            : "دلیل لغو",
        confirmText: "ثبت ",
        cancelText: "انصراف",
      });
      if (!note) return;
      await updateWithdrawStatus(id, selectedStatus, note);
    } else {
      await updateWithdrawStatus(id, selectedStatus);
    }
    swalSuccess({
      text: "وضعیت با موفقیت ذخیره شد",
    });
    loadData();
  };

  // محتوای مشترک سلکت + دکمه ذخیره (برای جدول و کارت)
  const renderStatusControl = (item) => {
    const locked = item.status === "done" || item.status === "canceled";
    return (
      <>
        <select
          className="finlist-select"
          disabled={locked}
          value={statusMap[item.id]}
          onChange={(e) =>
            setStatusMap((p) => ({ ...p, [item.id]: e.target.value }))
          }
        >
          <option value="pending">در حال بررسی</option>
          <option value="processing">در حال انجام</option>
          <option value="done">پرداخت شد</option>
          <option value="canceled">لغو شده</option>
        </select>
        <button
          disabled={locked}
          className="finlist-save-btn"
          onClick={() => handleSave(item.id)}
          aria-label="ذخیره"
          title="ذخیره"
        >
          <SaveIcon />
        </button>
      </>
    );
  };

  const renderAttachmentBtn = (item) => {
    const hasDoc = item.national_card_image || item.commitment_letter_image;
    return (
      <button
        className={`finlist-attachment ${hasDoc ? "has-some" : "has-none"}`}
        onClick={() => setSelectedItem(item)}
        title={hasDoc ? "مشاهده مدارک" : "مدرکی موجود نیست"}
        aria-label="مشاهده مدارک"
      >
        <Icon>{ATTACH_BTN}</Icon>
        {hasDoc && (
          <span className="finlist-attachment-badge">
            {item.national_card_image && item.commitment_letter_image
              ? "2"
              : "1"}
          </span>
        )}
      </button>
    );
  };

  const Pagination = () =>
    totalCount > 0 && (
      <div className="orders-pagination">
        <span className="pagination-info">
          {totalCount.toLocaleString("fa-IR")} درخواست — صفحه{" "}
          {pageSafe.toLocaleString("fa-IR")} از{" "}
          {totalPages.toLocaleString("fa-IR")}
        </span>
        <div className="pagination-controls">
          <button
            className="page-btn"
            disabled={pageSafe === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            aria-label="صفحه قبل"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
          {pageNumbers.map((n) => (
            <button
              key={n}
              className={`page-btn number${n === pageSafe ? " active" : ""}`}
              onClick={() => setPage(n)}
            >
              {n.toLocaleString("fa-IR")}
            </button>
          ))}
          <button
            className="page-btn"
            disabled={pageSafe === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            aria-label="صفحه بعد"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>
    );

  return (
    <div className="finlist-page">
      <h2 className="finlist-title">مدیریت درخواست‌های برداشت</h2>

      <AttachmentModal
        item={selectedItem}
        onClose={() => setSelectedItem(null)}
      />

      {/* ---- جدول دسکتاپ ---- */}
      <div className="finlist-table-wrapper">
        <table className="finlist-table">
          <thead>
            <tr>
              <th>موبایل</th>
              <th>مبلغ</th>
              <th>روش</th>
              <th>مقصد</th>
              <th>تاریخ</th>
              <th>وضعیت</th>
              <th>Note</th>
              <th>مدارک</th>
              <th>تغییر وضعیت</th>
              <th>ثبت</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} style={{ textAlign: "center" }}>
                  در حال بارگذاری...
                </td>
              </tr>
            ) : pagedItems.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: "center" }}>
                  هیچ درخواستی یافت نشد
                </td>
              </tr>
            ) : (
              pagedItems.map((item) => {
                const locked =
                  item.status === "done" || item.status === "canceled";
                return (
                  <tr key={item.id}>
                    <td>{item.user_phone}</td>
                    <td>{formatPrice(item.amount)} تومان</td>
                    <td>{methodLabel(item.method)}</td>
                    <td>{item.destination}</td>
                    <td>
                      {dayjs(item.created_at)
                        .calendar("jalali")
                        .format("YYYY/MM/DD HH:mm")}
                    </td>
                    <td>
                      <span className={`finlist-status-badge ${item.status}`}>
                        {statusLabel(item.status)}
                      </span>
                    </td>
                    <td>
                      {item.admin_note ? (
                        <button
                          className="finlist-note-btn"
                          onClick={() =>
                            swalWarning({
                              title: "توضیحات ادمین",
                              text: item.admin_note,
                            })
                          }
                        >
                          <NoteIcon />
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{renderAttachmentBtn(item)}</td>
                    <td>
                      <select
                        className="finlist-select"
                        disabled={locked}
                        value={statusMap[item.id]}
                        onChange={(e) =>
                          setStatusMap((p) => ({
                            ...p,
                            [item.id]: e.target.value,
                          }))
                        }
                      >
                        <option value="pending">در حال بررسی</option>
                        <option value="processing">در حال انجام</option>
                        <option value="done">پرداخت شد</option>
                        <option value="canceled">لغو شده</option>
                      </select>
                    </td>
                    <td>
                      <button
                        disabled={locked}
                        className="finlist-save-btn"
                        onClick={() => handleSave(item.id)}
                        aria-label="ذخیره"
                        title="ذخیره"
                      >
                        <SaveIcon />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {!loading && <Pagination />}
      </div>

      {/* ---- کارت‌های موبایل ---- */}
      <div className="finlist-cards">
        {loading ? (
          <p className="finlist-cards-msg">در حال بارگذاری...</p>
        ) : pagedItems.length === 0 ? (
          <p className="finlist-cards-msg">هیچ درخواستی یافت نشد</p>
        ) : (
          pagedItems.map((item) => (
            <div className="finlist-card" key={item.id}>
              <div className="finlist-card-row">
                <span className="finlist-card-label">موبایل</span>
                <span className="finlist-card-value">{item.user_phone}</span>
              </div>
              <div className="finlist-card-row">
                <span className="finlist-card-label">مبلغ</span>
                <span className="finlist-card-value">
                  {formatPrice(item.amount)} تومان
                </span>
              </div>
              <div className="finlist-card-row">
                <span className="finlist-card-label">روش</span>
                <span className="finlist-card-value">
                  {methodLabel(item.method)}
                </span>
              </div>
              <div className="finlist-card-row">
                <span className="finlist-card-label">مقصد</span>
                <span className="finlist-card-value">{item.destination}</span>
              </div>
              <div className="finlist-card-row">
                <span className="finlist-card-label">تاریخ</span>
                <span className="finlist-card-value">
                  {dayjs(item.created_at)
                    .calendar("jalali")
                    .format("YYYY/MM/DD HH:mm")}
                </span>
              </div>
              <div className="finlist-card-row">
                <span className="finlist-card-label">وضعیت</span>
                <span className={`finlist-status-badge ${item.status}`}>
                  {statusLabel(item.status)}
                </span>
              </div>
              <div className="finlist-card-row">
                <span className="finlist-card-label">توضیحات ادمین</span>
                <span className="finlist-card-value">
                  {item.admin_note ? (
                    <button
                      className="finlist-note-btn"
                      onClick={() =>
                        swalWarning({
                          title: "توضیحات ادمین",
                          text: item.admin_note,
                        })
                      }
                    >
                      <NoteIcon />
                    </button>
                  ) : (
                    "-"
                  )}
                </span>
              </div>
              <div className="finlist-card-row">
                <span className="finlist-card-label">مدارک</span>
                <span className="finlist-card-value">
                  {renderAttachmentBtn(item)}
                </span>
              </div>
              <div className="finlist-card-divider" />
              <div className="finlist-card-actions">
                {renderStatusControl(item)}
              </div>
            </div>
          ))
        )}

        {!loading && <Pagination />}
      </div>
    </div>
  );
}
